import Box from '@mui/material/Box'
import { type EnrollmentData, NewEnrollmentStatus } from 'core/types'
import { Modal } from 'Components/Modal'
import { EnrollmentChip } from 'Components/Enrollment/EnrollmentChip'
import { ProgramVerificationChip } from 'Components/Program/ProgramVerificationChip'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { toast } from 'react-toastify'
import { useEffect, useState } from 'react'
import { useRefreshDispatch } from 'ContextProviders/RefreshProvider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { type SelectOption, SelectWithLabel } from 'Components/SelectWithLabel'
import { DatePickerWithLabel } from 'Components/DatePicker'
import { TextAreaWithLabel } from 'Components/TextArea'
import { CareManagementSummary } from 'Components/CareManagement/CareManagementSummary'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import S3Image from 'Components/S3Image'
import BusinessIcon from '@mui/icons-material/Business'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import EventIcon from '@mui/icons-material/Event'

interface Props {
  open: boolean
  enrollmentData: EnrollmentData
  onClose: () => void
}

interface FormFields {
  exitReasonId?: number
  exitDate: Date | null
  notes: string
}

const ExitEnrollmentModal = (props: Props): JSX.Element => {
  const { open, enrollmentData, onClose } = props
  const [exitReasons, setExitReasons] = useState<Array<SelectOption<number>>>([])
  const [formFields, setFormFields] = useState<FormFields>({
    exitReasonId: undefined,
    exitDate: null,
    notes: ''
  })
  const disp = useRefreshDispatch()
  const refresh = (): void => { disp({ type: 'refresh' }) }

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response, success } = await sendGet('/Enrollment/GetExitReasons', {})
      if (success) {
        setExitReasons(response)
      } else {
        toast.error('Failed to load exit reasons')
      }
    }

    void fetchData()
  }, [])

  const handleExitEnrollment = async (): Promise<void> => {
    if (formFields.exitReasonId === undefined || formFields.exitDate === null || formFields.notes === '') {
      toast.error('All fields are required')
      return
    }

    const { response } = await sendPost(`/NewEnrollment/ExitEnrollment?id=${enrollmentData.id}`, formFields)
    if (response.success === true) {
      toast.success(response.message)
      onClose()
      refresh()
    } else {
      response.errors.forEach((e: any) => toast.error(e.error))
    }
  }

  return <Modal
    onClose={onClose}
    open={open}
    onConfirm={handleExitEnrollment}
    title='Exit Enrollment'
    confirmButtonText='Exit Enrollment'
    cancelButtonText='Cancel'
    confirmationContent={
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant='body1' sx={{ fontWeight: 'bold' }}>{enrollmentData.providerName}</Typography>
        <Stack direction='column' spacing={1}>
          <Stack direction='row' spacing={1}>
            {enrollmentData.serviceS3Key != null ? <S3Image style={{ width: '30px', height: '30px  ' }} imageKey={enrollmentData.serviceS3Key} /> : <SchoolOutlinedIcon sx={{ width: '30px', height: '30px' }} />}
            <Typography>{enrollmentData.serviceName}</Typography>
            <ProgramVerificationChip programName={enrollmentData.programName} status={enrollmentData.programVerificationStatus} timeSpanName={enrollmentData.programTimeSpanName} showStatusName={true} />
          </Stack>
          <Stack direction='row' spacing={1}>
            <BusinessIcon sx={{ width: '30px', height: '30px' }} />
            <Typography>{enrollmentData.facilityName ?? 'Not Selected'}</Typography>
          </Stack>
          {enrollmentData.sessionBlockName !== null && <Stack direction='row' spacing={1}>
            <EventIcon sx={{ width: '30px', height: '30px' }} />
            <Typography>{enrollmentData.sessionBlockName ?? 'Not Selected'}</Typography>
          </Stack>}
          {enrollmentData.deliverySchedule !== null && <Stack direction='row' spacing={1}>
            <EventAvailableIcon sx={{ width: '30px', height: '30px' }} />
            <Typography>{enrollmentData.deliverySchedule ?? 'Not Selected'}</Typography>
          </Stack>}
        </Stack>
        <Box>
          <SelectWithLabel
            width='100%'
            labelContent={<Typography variant='subtitle2'>Reason</Typography>}
            value={formFields.exitReasonId}
            onChange={(e) => { setFormFields({ ...formFields, exitReasonId: Number(e.target.value) }) }}
            name={'exitReason'}
            options={exitReasons}
          />
        </Box>
        <Box>
          <DatePickerWithLabel
            labelContent={<Typography variant='subtitle2'>Exit Date</Typography>}
            value={formFields.exitDate}
            onChange={(newDate) => { setFormFields({ ...formFields, exitDate: newDate }) }}
            name={'exitDate'}
          />
        </Box>
        <Box>
          <TextAreaWithLabel
            label='Notes'
            toolTip='Any additional notes regarding the exit'
            value={formFields.notes}
            onChange={(e) => { setFormFields({ ...formFields, notes: e.target.value }) }}
            name={'notes'}
          />
        </Box>
        <CareManagementSummary childID={enrollmentData.childId} childName={enrollmentData.childName} variant='card' />
        <Stack direction='row' spacing={2}>
          <Typography>New Enrollment Status</Typography>
          <EnrollmentChip statusId={NewEnrollmentStatus.Exited} />
        </Stack>
      </Box>
    }
  />
}

export default ExitEnrollmentModal
